import React, { useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Home, FileText, CheckCircle, Printer } from 'lucide-react';
import { useSales } from '../../../../../../contexts/SalesContext';
import { useBusiness } from '../../../../../../contexts/BusinessContext';
import { usePrinters } from '../../../../../../hooks/usePrinters';
import { ticketRenderer } from '../../../../../../services/ticketRenderer';

const SuccessScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getSale } = useSales();
  const { business } = useBusiness();
  const { updatePrinterStatus } = usePrinters();
  const sale = getSale(id);
  const hasPrinted = useRef(false);

  useEffect(() => {
    const printSaleTicket = async () => {
      if (!sale || !business?.id || hasPrinted.current) return;

      // Solo imprimir si la venta está confirmada
      if (sale.status === 'pending') {
        console.log('Venta pendiente - No se imprime ticket');
        return;
      }

      try {
        hasPrinted.current = true;
        console.log('=== DEBUG IMPRESIÓN DE VENTA CONFIRMADA ===');
        console.log('Business:', {
          id: business?.id,
          name: business?.name
        });
        console.log('Sale:', {
          id: sale.id,
          items: sale.items?.length,
          total: sale.total_amount
        });

        // Preparar los datos para el ticket
        const customer = sale.customer_name ? { name: sale.customer_name } : null;
        
        // Generar el ticket utilizando el nuevo sistema de templates
        const ticketContent = ticketRenderer.generateTicket(
          {
            business,
            sale,
            items: sale.items,
            customer
          },
          'delivery', // Tipo de ticket
          'plain'     // Formato (plain o escpos)
        );

        console.log('Contenido del ticket:', ticketContent);

        // Enviar a imprimir - El printer_id "3" es usado por el printer client para identificar la impresora
        // El printer client usa la IP configurada en su variable de entorno PRINTER_IP
        console.log('Enviando petición a:', 'https://tibo-printer-service.fly.dev/print');
        const response = await fetch('https://tibo-printer-service.fly.dev/print', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            printer_id: "3",  // Este ID es interpretado por el printer client
            business_id: business?.id,
            content: ticketContent,
            type: "delivery"
          })
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Error response:', {
            status: response.status,
            statusText: response.statusText,
            body: errorText
          });
          await updatePrinterStatus("3", false);
          throw new Error('Error al imprimir el ticket');
        }

        const data = await response.json();
        if (data.error) {
          await updatePrinterStatus("3", false);
          throw new Error(data.error);
        }

        // Si llegamos aquí, la impresión fue exitosa
        await updatePrinterStatus("3", true);

        console.log("✅ Ticket de venta enviado a imprimir!");
      } catch (error) {
        console.error('❌ Error al imprimir ticket:', error);
        hasPrinted.current = false;
      }
    };

    // Intentar imprimir
    printSaleTicket();
  }, [id]);

  // Nuevo efecto para el redireccionamiento automático
  useEffect(() => {
    const timer = setTimeout(() => {
      navigate('/sales');
    }, 3000);

    return () => clearTimeout(timer);
  }, [navigate]);

  const handleClose = () => {
    navigate('/sales');
  };

  const handleViewDetails = (saleId) => {
    navigate(`/sales/${saleId}`);
  };

  // Si no hay venta, mostrar mensaje y botón para volver
  if (!sale) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-800 flex flex-col items-center justify-center p-8">
        <div className="text-center space-y-4">
          <div className="text-gray-500 dark:text-gray-400">No se encontró la venta</div>
          <button
            onClick={handleClose}
            className="px-4 py-2 bg-blue-500 dark:bg-blue-600 text-white rounded-lg hover:bg-blue-600 dark:hover:bg-blue-700"
          >
            Volver a ventas
          </button>
        </div>
      </div>
    );
  }

  const isPending = sale.status === 'pending';

  return (
    <div className="min-h-screen bg-white dark:bg-gray-800 flex flex-col overflow-auto">
      {/* Content */}
      <div className="py-8 px-4 flex flex-col items-center">
        <div className={`w-40 h-40 rounded-full flex items-center justify-center mb-8 ${
          isPending ? 'bg-amber-100 dark:bg-amber-900/50' : 'bg-green-100 dark:bg-green-900/50'
        }`}>
          <CheckCircle className={`w-24 h-24 ${
            isPending ? 'text-amber-500 dark:text-amber-400' : 'text-green-500 dark:text-green-400'
          }`} />
        </div>

        <h1 className="text-4xl font-bold mb-4 text-gray-900 dark:text-white text-center">
          {isPending ? '¡Venta guardada!' : '¡Venta confirmada!'}
        </h1>
        
        <p className="text-gray-500 dark:text-gray-400 text-center max-w-sm mb-12 text-lg">
          {isPending 
            ? 'La venta se guardó como pendiente. Podrás confirmarla más tarde desde la lista de ventas.'
            : 'La venta se registró correctamente y los productos ya fueron descontados del inventario.'
          }
        </p>

        {!isPending && (
          <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-8 bg-gray-50 dark:bg-gray-700/50 px-6 py-4 rounded-xl w-full max-w-sm">
            <Printer className="w-6 h-6 text-gray-400 dark:text-gray-500" />
            <span className="text-lg">Imprimiendo tickets automáticamente...</span>
          </div>
        )}

        {sale.customer_name && (
          <div className="text-lg text-gray-500 dark:text-gray-400 mb-8">
            Cliente: <span className="font-medium text-gray-900 dark:text-white">{sale.customer_name}</span>
          </div>
        )}

        {/* Action Buttons - Moved up and made more prominent */}
        <div className="w-full max-w-lg px-4 flex gap-4 mb-8">
          <button
            onClick={handleClose}
            className={`flex-1 py-4 rounded-xl text-white text-lg font-medium flex items-center justify-center gap-3 shadow-lg hover:shadow-xl ${
              isPending 
                ? 'bg-amber-500 dark:bg-amber-600 hover:bg-amber-600 dark:hover:bg-amber-700' 
                : 'bg-blue-500 dark:bg-blue-600 hover:bg-blue-600 dark:hover:bg-blue-700'
            } transition-colors`}
          >
            <Home className="w-6 h-6" />
            <span>Ir a ventas</span>
          </button>
          <button
            onClick={() => handleViewDetails(sale.id)}
            className="flex-1 py-4 bg-green-500 dark:bg-green-600 text-white rounded-xl flex items-center justify-center gap-3 hover:bg-green-600 dark:hover:bg-green-700 transition-colors text-lg font-medium shadow-lg hover:shadow-xl"
          >
            <FileText className="w-6 h-6" />
            <span>Ver detalles</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SuccessScreen; 